import { useState, useEffect, useRef } from "react";
import { Service } from "@/types";
import { useServicesStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface LabTestSelectorProps {
  value: string;
  onChange: (value: string) => void;
}

export function LabTestSelector({ value, onChange }: LabTestSelectorProps) {
  const { services, fetchServices } = useServicesStore();
  const [selectedTests, setSelectedTests] = useState<string[]>(value ? value.split(', ').filter(Boolean) : []);
  const [currentTestInput, setCurrentTestInput] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Fetch services when component mounts
  useEffect(() => {
    fetchServices();
  }, []);
  
  // Filter services to only include laboratory tests
  const labTests = services.filter(service => 
    service.category?.toLowerCase().includes('lab') || 
    service.category?.toLowerCase().includes('test')
  );

  // Filter tests based on input (if input is empty, show all tests)
  const filteredTests = currentTestInput.trim().length > 0
    ? labTests.filter(test => 
        test.name.toLowerCase().includes(currentTestInput.toLowerCase())
      )
    : labTests; // Show all tests when input is empty

  // Check if we should show "Add Custom" option
  const hasMatches = filteredTests.length > 0;
  const canAddCustom = currentTestInput.trim().length > 0 && filteredTests.length === 0;

  useEffect(() => {
    // Update the parent component value when selectedTests changes
    onChange(selectedTests.join(', '));
  }, [selectedTests, onChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCurrentTestInput(value);
    setShowDropdown(true);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
    setHighlightedIndex(-1);
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for click selection
    setTimeout(() => {
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }, 200);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return;

    const options = hasMatches ? filteredTests : [];

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (hasMatches) {
          setHighlightedIndex(prev => 
            prev < options.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (hasMatches) {
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : options.length - 1
          );
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (hasMatches && highlightedIndex >= 0 && highlightedIndex < options.length) {
          handleSelectTest(options[highlightedIndex].name);
        } else if (canAddCustom) {
          handleAddCustomTest();
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        setHighlightedIndex(-1);
        setCurrentTestInput("");
        break;
    }
  };

  const handleSelectTest = (testName: string) => {
    if (!selectedTests.includes(testName)) {
      const updatedTests = [...selectedTests, testName];
      setSelectedTests(updatedTests);
    }
    setCurrentTestInput("");
    setShowDropdown(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const handleAddCustomTest = () => {
    const testName = currentTestInput.trim();
    if (testName && !selectedTests.includes(testName)) {
      const updatedTests = [...selectedTests, testName];
      setSelectedTests(updatedTests);
      setCurrentTestInput("");
      setShowDropdown(false);
      setHighlightedIndex(-1);
      inputRef.current?.focus();
    }
  };

  const handleAddButtonClick = () => {
    if (hasMatches && highlightedIndex >= 0 && highlightedIndex < filteredTests.length) {
      handleSelectTest(filteredTests[highlightedIndex].name);
    } else if (hasMatches && filteredTests.length > 0) {
      handleSelectTest(filteredTests[0].name);
    } else if (canAddCustom) {
      handleAddCustomTest();
    }
  };

  const handleRemoveTest = (test: string) => {
    const updatedTests = selectedTests.filter(t => t !== test);
    setSelectedTests(updatedTests);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-3 relative">
        <div className="flex-[2] relative">
          <Input
            ref={inputRef}
            value={currentTestInput}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            placeholder="Type to search lab tests or enter custom test..."
            className="w-full"
          />
          
          {/* Dropdown opens upward */}
          {showDropdown && (
            <div 
              ref={dropdownRef}
              className="absolute z-50 w-full bottom-full mb-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
              role="listbox"
              aria-label="Lab test options"
            >
              {hasMatches ? (
                filteredTests.map((test, index) => (
                  <div
                    key={test.id}
                    className={`px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                      index === highlightedIndex 
                        ? 'bg-blue-100 text-blue-900' 
                        : 'hover:bg-gray-100'
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectTest(test.name);
                    }}
                    role="option"
                    aria-selected={index === highlightedIndex}
                    tabIndex={-1}
                  >
                    <span className="text-sm">{test.name}</span>
                  </div>
                ))
              ) : canAddCustom ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  No matching test found. Press Enter to add as custom test.
                </div>
              ) : labTests.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  {services.length === 0 ? (
                    "Loading laboratory tests..."
                  ) : (
                    "No laboratory tests found. Add services with 'Lab Test' or 'Test' category in Settings > Services."
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>
        <div className="flex-[1]">
          <Button 
            type="button" 
            onClick={handleAddButtonClick}
            disabled={!hasMatches && !canAddCustom}
            className="w-full h-10"
          >
            <Plus className="h-4 w-4 mr-1" />
            {canAddCustom ? "Add Custom" : "Add"}
          </Button>
        </div>
      </div>
      
      {selectedTests.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700">Selected Laboratory Tests</Label>
          <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-md border">
            {selectedTests.map((test, index) => (
              <Badge key={index} variant="secondary" className="flex items-center gap-1">
                {test}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-4 w-4 p-0 hover:bg-transparent" 
                  onClick={() => handleRemoveTest(test)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
